import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getContent, listJournal } from "@/lib/api";

/**
 * SiteContent — the storefront-wide store for admin-editable copy and settings.
 *
 * ── FOUC prevention ────────────────────────────────────────────────
 * The API roundtrip for `/api/content` takes ~150–400ms. Rendering
 * with an empty `overrides` object during that window causes visible
 * flicker (e.g. "Summer Sale" flashing to "Summer Collection"). We
 * cache the last known payload in localStorage synchronously so the
 * first paint uses it, then refresh in the background.
 */

const DEFAULT_HERO =
  "https://customer-assets.emergentagent.com/job_lisboa-boutique-ui/artifacts/bam940fn_Outpaint_image_to_Greek_islands_202607111214.webp";

const DEFAULT_SETTINGS = {
  hero_image_url: DEFAULT_HERO,
  hero_image_position: "40% 45%",
  hero_image_position_mobile: "75% 45%",
  hero_image_scale: 1.0,
  hero_image_scale_mobile: 1.0,
  instagram_gallery: [],
  category_images: {},
  announcement_bar_enabled: false,
  announcement_bar_msg2_enabled: true,
  announcement_bar_text: { en: "", pt: "", es: "" },
  free_shipping_threshold: 150,
  hero_title_scale: 1.0,
  hero_title_color: null,
  hero_cta_color: null,
  hero_subtitle_enabled: true,
  collection_title_enabled: true,
  maintenance_mode: false,
  maintenance_message: {},
  show_stock_left: true,
  stock_left_threshold: 3,
  trending_enabled: true,
};

const CACHE_KEY = "gl_site_content_v1";

const readCache = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage?.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      overrides: parsed.overrides && typeof parsed.overrides === "object" ? parsed.overrides : {},
      settings: {
        hero_image_url: parsed.settings?.hero_image_url || DEFAULT_HERO,
        hero_image_position: parsed.settings?.hero_image_position || DEFAULT_SETTINGS.hero_image_position,
        hero_image_position_mobile: parsed.settings?.hero_image_position_mobile || DEFAULT_SETTINGS.hero_image_position_mobile,
        hero_image_scale: typeof parsed.settings?.hero_image_scale === "number" ? parsed.settings.hero_image_scale : 1.0,
        hero_image_scale_mobile: typeof parsed.settings?.hero_image_scale_mobile === "number" ? parsed.settings.hero_image_scale_mobile : 1.0,
        instagram_gallery: Array.isArray(parsed.settings?.instagram_gallery) ? parsed.settings.instagram_gallery : [],
        category_images: parsed.settings?.category_images && typeof parsed.settings.category_images === "object" ? parsed.settings.category_images : {},
        announcement_bar_enabled: Boolean(parsed.settings?.announcement_bar_enabled),
        announcement_bar_msg2_enabled: parsed.settings?.announcement_bar_msg2_enabled !== false,
        announcement_bar_text: parsed.settings?.announcement_bar_text && typeof parsed.settings.announcement_bar_text === "object" ? parsed.settings.announcement_bar_text : { en: "", pt: "", es: "" },
        free_shipping_threshold: typeof parsed.settings?.free_shipping_threshold === "number" ? parsed.settings.free_shipping_threshold : DEFAULT_SETTINGS.free_shipping_threshold,
        hero_title_scale: typeof parsed.settings?.hero_title_scale === "number" ? parsed.settings.hero_title_scale : 1.0,
        hero_title_color: typeof parsed.settings?.hero_title_color === "string" ? parsed.settings.hero_title_color : null,
        hero_cta_color: typeof parsed.settings?.hero_cta_color === "string" ? parsed.settings.hero_cta_color : null,
        hero_subtitle_enabled: parsed.settings?.hero_subtitle_enabled !== false,
        collection_title_enabled: parsed.settings?.collection_title_enabled !== false,
        maintenance_mode: Boolean(parsed.settings?.maintenance_mode),
        maintenance_message: parsed.settings?.maintenance_message && typeof parsed.settings.maintenance_message === "object" ? parsed.settings.maintenance_message : {},
        show_stock_left: parsed.settings?.show_stock_left !== false,   // default true
        stock_left_threshold: typeof parsed.settings?.stock_left_threshold === "number" ? parsed.settings.stock_left_threshold : 3,
        trending_enabled: parsed.settings?.trending_enabled !== false,
        sale_banner: parsed.settings?.sale_banner && typeof parsed.settings.sale_banner === "object" ? parsed.settings.sale_banner : null,
      },
    };
  } catch (_e) {
    return null;
  }
};

const writeCache = (overrides, settings) => {
  try {
    window.localStorage?.setItem(CACHE_KEY, JSON.stringify({ overrides, settings }));
  } catch (_e) { /* quota exceeded / SSR — safe to ignore */ }
};

const SiteContentCtx = createContext({
  overrides: {},
  settings: DEFAULT_SETTINGS,
  refresh: () => {},
  journalCount: 0,
});

export const SiteContentProvider = ({ children }) => {
  // Pre-seed from cache so first paint already reflects the last known admin overrides.
  const cached = readCache();
  const [overrides, setOverrides] = useState(cached?.overrides || {});
  const [settings, setSettings] = useState(cached?.settings || DEFAULT_SETTINGS);
  // journalCount drives whether the "Journal" nav link is surfaced. We
  // start optimistically at 0 so the link is HIDDEN until the API
  // confirms at least one published entry — better to briefly hide a
  // legit link than to briefly show a broken one that goes to an empty
  // page.
  const [journalCount, setJournalCount] = useState(0);

  const load = useCallback(async () => {
    try {
      const data = await getContent();
      const nextOverrides = data.overrides || {};
      const nextSettings = {
        hero_image_url: data.settings?.hero_image_url || DEFAULT_HERO,
        hero_image_position: data.settings?.hero_image_position || DEFAULT_SETTINGS.hero_image_position,
        hero_image_position_mobile: data.settings?.hero_image_position_mobile || DEFAULT_SETTINGS.hero_image_position_mobile,
        hero_image_scale: typeof data.settings?.hero_image_scale === "number" ? data.settings.hero_image_scale : 1.0,
        hero_image_scale_mobile: typeof data.settings?.hero_image_scale_mobile === "number" ? data.settings.hero_image_scale_mobile : 1.0,
        instagram_gallery: Array.isArray(data.settings?.instagram_gallery) ? data.settings.instagram_gallery : [],
        category_images: data.settings?.category_images && typeof data.settings.category_images === "object" ? data.settings.category_images : {},
        announcement_bar_enabled: Boolean(data.settings?.announcement_bar_enabled),
        announcement_bar_msg2_enabled: data.settings?.announcement_bar_msg2_enabled !== false,
        announcement_bar_text: data.settings?.announcement_bar_text && typeof data.settings.announcement_bar_text === "object" ? data.settings.announcement_bar_text : { en: "", pt: "", es: "" },
        free_shipping_threshold: typeof data.settings?.free_shipping_threshold === "number" ? data.settings.free_shipping_threshold : DEFAULT_SETTINGS.free_shipping_threshold,
        hero_title_scale: typeof data.settings?.hero_title_scale === "number" ? data.settings.hero_title_scale : 1.0,
        hero_title_color: typeof data.settings?.hero_title_color === "string" ? data.settings.hero_title_color : null,
        hero_cta_color: typeof data.settings?.hero_cta_color === "string" ? data.settings.hero_cta_color : null,
        hero_subtitle_enabled: data.settings?.hero_subtitle_enabled !== false,
        collection_title_enabled: data.settings?.collection_title_enabled !== false,
        maintenance_mode: Boolean(data.settings?.maintenance_mode),
        maintenance_message: data.settings?.maintenance_message && typeof data.settings.maintenance_message === "object" ? data.settings.maintenance_message : {},
        show_stock_left: data.settings?.show_stock_left !== false,   // default true
        stock_left_threshold: typeof data.settings?.stock_left_threshold === "number" ? data.settings.stock_left_threshold : 3,
        trending_enabled: data.settings?.trending_enabled !== false,
        sale_banner: data.settings?.sale_banner && typeof data.settings.sale_banner === "object" ? data.settings.sale_banner : null,
      };
      setOverrides(nextOverrides);
      setSettings(nextSettings);
      writeCache(nextOverrides, nextSettings);
    } catch (_e) {
      // Fail silently — the site keeps the cached (or compiled default) copy.
    }
  }, []);

  // Fetch the journal count once on mount. We only need the length; the
  // Home page already fetches the same endpoint on its own, so this is a
  // cheap parallel request. Any hard failure just leaves count = 0
  // (link stays hidden) which is the safe default.
  useEffect(() => {
    let cancelled = false;
    listJournal()
      .then((rows) => { if (!cancelled) setJournalCount(Array.isArray(rows) ? rows.length : 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { load(); }, [load]);

  const value = useMemo(
    () => ({ overrides, settings, refresh: load, journalCount }),
    [overrides, settings, load, journalCount]
  );
  return <SiteContentCtx.Provider value={value}>{children}</SiteContentCtx.Provider>;
};

export const useSiteContent = () => useContext(SiteContentCtx);
